import { Suspense, useState, useRef, useEffect, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { Mesh, Group, Fog, Color } from 'three';
import { useGachaDraw } from '../../hooks/useInventory';
import { getErrorMessage } from '../../utils/errorMessages';
import type { CardSummary } from '../../types/cards';
import { GachaAnimation } from './GachaAnimation';
import { DonationSection } from './DonationSection';
import { isMobile, getOptimalDPR } from '../../utils/deviceDetection';
import './GachaMachine3D.css';

interface GachaMachine3DProps {
  isAuthenticated: boolean;
  currency?: number;
}

const SUMMON_COST = 10;

// 3D 소환 오브젝트 (제단 중앙의 신비로운 돌)
function SummonOrb({ isActivated, isSpinning }: {
  isActivated: boolean;
  isSpinning: boolean;
}) {
  const orbRef = useRef<Group>(null);
  const baseRef = useRef<Mesh>(null);

  useFrame((state) => {
    if (orbRef.current) {
      // 기본 회전 애니메이션
      orbRef.current.rotation.y += 0.01;
      
      if (isActivated) {
        // 활성화 시 위로 떠오르는 효과
        orbRef.current.position.y = Math.sin(state.clock.elapsedTime * 3) * 0.2 + 0.3;
        orbRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 5) * 0.1;
      } else if (isSpinning) {
        // 소환 중 빠른 회전
        orbRef.current.rotation.y += 0.1;
        orbRef.current.position.y = Math.sin(state.clock.elapsedTime * 8) * 0.15;
      } else {
        orbRef.current.position.y = 0;
      }
    }

    if (baseRef.current && isSpinning) {
      // 제단이 작동 중일 때 약간의 흔들림
      baseRef.current.rotation.z = Math.sin(state.clock.elapsedTime * 15) * 0.02;
    }
  });

  return (
    <group position={[0, 0, 0]}>
      {/* 제단 기반 */}
      <mesh ref={baseRef} position={[0, -1.2, 0]}>
        <cylinderGeometry args={[0.8, 1, 0.4, 16]} />
        <meshStandardMaterial 
          color="#c97d60" 
          metalness={0.3} 
          roughness={0.7}
          emissive="#d4af37"
          emissiveIntensity={isSpinning ? 0.3 : 0.1}
        />
      </mesh>

      {/* 소환 오브 (황금 구체) */}
      <group ref={orbRef} position={[0, -0.8, 0]}>
        <mesh position={[0, 0.5, 0]}>
          <sphereGeometry args={[0.3, 32, 32]} />
          <meshStandardMaterial 
            color={isSpinning ? "#ffd700" : "#d4af37"} 
            metalness={0.9} 
            roughness={0.1}
            emissive={isSpinning ? "#ffd700" : "#d4af37"}
            emissiveIntensity={isSpinning ? 0.8 : 0.4}
          />
        </mesh>
        {/* 내부 빛 효과 */}
        {isSpinning && (
          <mesh position={[0, 0.5, 0]}>
            <sphereGeometry args={[0.25, 16, 16]} />
            <meshStandardMaterial 
              color="#fff8dc" 
              transparent
              opacity={0.6}
              emissive="#fff8dc"
              emissiveIntensity={1}
            />
        </mesh>
        )}
      </group>
    </group>
  );
}

// 3D 사막 제단 본체
function AltarBody({ isSpinning }: { isSpinning: boolean }) {
  const altarRef = useRef<Group>(null);

  useFrame((state) => {
    if (altarRef.current && isSpinning) {
      // 소환 중 약간의 흔들림
      altarRef.current.rotation.z = Math.sin(state.clock.elapsedTime * 20) * 0.01;
      altarRef.current.position.y = Math.sin(state.clock.elapsedTime * 15) * 0.02;
    }
  });

  return (
    <group ref={altarRef}>
      {/* 제단 기반 (사각형 기둥) */}
      <mesh position={[0, -0.5, 0]}>
        <boxGeometry args={[2.5, 1, 2.5]} />
        <meshStandardMaterial
          color="#c9a961"
          metalness={0.2}
          roughness={0.8}
        />
      </mesh>

      {/* 제단 상단 (계단식) */}
      <mesh position={[0, 0.2, 0]}>
        <boxGeometry args={[2, 0.3, 2]} />
        <meshStandardMaterial
          color="#d4af37"
          metalness={0.4}
          roughness={0.6}
        />
      </mesh>

      {/* 제단 중앙 원형 플랫폼 */}
      <mesh position={[0, 0.35, 0]} rotation={[0, 0, 0]}>
        <cylinderGeometry args={[0.9, 0.9, 0.1, 32]} />
        <meshStandardMaterial
          color={isSpinning ? '#ffd700' : '#d4af37'}
          metalness={0.6}
          roughness={0.4}
          emissive={isSpinning ? '#ffd700' : '#d4af37'}
          emissiveIntensity={isSpinning ? 0.4 : 0.1}
        />
      </mesh>

      {/* 제단 측면 장식 (고대 문양) */}
      <mesh position={[-1.25, -0.2, 0]} rotation={[0, 0, Math.PI / 2]}>
        <boxGeometry args={[0.15, 1, 2.5]} />
        <meshStandardMaterial
          color="#c97d60"
          metalness={0.3}
          roughness={0.7}
        />
      </mesh>
      <mesh position={[1.25, -0.2, 0]} rotation={[0, 0, Math.PI / 2]}>
        <boxGeometry args={[0.15, 1, 2.5]} />
        <meshStandardMaterial
          color="#c97d60"
          metalness={0.3}
          roughness={0.7}
        />
      </mesh>

      {/* 제단 앞뒤 장식 */}
      <mesh position={[0, -0.2, 1.25]}>
        <boxGeometry args={[2.5, 1, 0.15]} />
        <meshStandardMaterial
          color="#c97d60"
          metalness={0.3}
          roughness={0.7}
        />
      </mesh>
      <mesh position={[0, -0.2, -1.25]}>
        <boxGeometry args={[2.5, 1, 0.15]} />
        <meshStandardMaterial
          color="#c97d60"
          metalness={0.3}
          roughness={0.7}
        />
      </mesh>
    </group>
  );
}

// 카메라 애니메이션 컴포넌트
function AnimatedCamera({ 
  isAnimating, 
  isSpinning 
}: { 
  isAnimating: boolean; 
  isSpinning: boolean;
}) {
  const cameraProgress = useRef(0);

  useFrame((state) => {
    const camera = state.camera;
    
    if (isAnimating || isSpinning) {
      // 애니메이션 진행도 업데이트
      if (isSpinning) {
        cameraProgress.current = Math.min(1, cameraProgress.current + 0.05);
      }
      
      if (isSpinning && cameraProgress.current > 0) {
        // 제단 중앙으로 줌인하는 애니메이션 (사선 유지)
        const targetPosition = { x: 1.5, y: 1.2, z: 4 };
        camera.position.lerp(targetPosition, 0.08);
        camera.lookAt(0, -0.5, 0);
      } else if (isAnimating) {
        // 애니메이션 시작 - 카메라가 제단 중앙으로 이동 시작 (사선 유지)
        const targetPosition = { x: 2, y: 1.3, z: 5 };
        camera.position.lerp(targetPosition, 0.06);
        camera.lookAt(0, -0.3, 0);
      }
    } else {
      // 기본 위치로 부드럽게 복귀 (사선 시점)
      cameraProgress.current = 0;
      const defaultPosition = { x: 2.5, y: 1.5, z: 5 };
      camera.position.lerp(defaultPosition, 0.05);
      
      // 기본 상태에서 미묘한 카메라 움직임
      const time = state.clock.elapsedTime;
      const subtleX = Math.sin(time * 0.2) * 0.1;
      const subtleY = Math.cos(time * 0.15) * 0.1;
      camera.lookAt(subtleX, subtleY, 0);
    }
  });

  return null;
}

// 배경 설정 컴포넌트
function SceneBackground() {
  const { scene } = useThree();
  
  useEffect(() => {
    scene.background = new Color('#ffd89b');
    scene.fog = new Fog('#ffd89b', 15, 30);
    
    return () => {
      scene.background = null;
      scene.fog = null;
    };
  }, [scene]);
  
  return null;
}

// 3D 장면
function SummonAltarScene({
  isActivated,
  isSpinning,
  isAnimating,
}: {
  isActivated: boolean;
  isSpinning: boolean;
  isAnimating: boolean;
}) {
  const sceneRef = useRef<Group>(null);

  useFrame((state) => {
    if (sceneRef.current && !isSpinning && !isAnimating) {
      // 기본 상태에서 미묘한 회전 (자동 회전)
      const time = state.clock.elapsedTime;
      sceneRef.current.rotation.y = Math.sin(time * 0.3) * 0.15;
    } else if (sceneRef.current && isSpinning) {
      // 소환 중에는 회전 멈춤
      sceneRef.current.rotation.y = 0;
    }
  });

  return (
    <group ref={sceneRef}>
      {/* 사막 조명 */}
      <ambientLight intensity={0.7} color="#fff8dc" />
      <directionalLight position={[5, 8, 5]} intensity={1.2} color="#ffd700" />
      <pointLight position={[-3, 5, -3]} intensity={0.6} color="#ff8c42" />
      <pointLight position={[0, 4, 3]} intensity={0.9} color="#ffd700" />

      {/* 제단 주변 빛 효과 */}
      {isSpinning && (
        <pointLight position={[0, 0, 0]} intensity={2} color="#ffd700" distance={5} />
      )}

      {/* 사막 지면 */}
      <mesh position={[0, -0.5, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[50, 50]} />
        <meshStandardMaterial 
          color="#c97d60" 
          roughness={0.9}
          metalness={0.1}
        />
      </mesh>

      <AltarBody isSpinning={isSpinning} />
      <SummonOrb
        isActivated={isActivated}
        isSpinning={isSpinning}
      />

      {/* 제목 텍스트 */}
      <Text
        position={[0, 1.5, 0.5]}
        fontSize={0.3}
        color="#d4af37"
        anchorX="center"
        anchorY="middle"
        fontWeight="bold"
        outlineWidth={0.02}
        outlineColor="#6b5d4f"
      >
        소환의 제단
      </Text>
    </group>
  );
}

export function GachaMachine3D({ isAuthenticated, currency }: GachaMachine3DProps) {
  const [pulls, setPulls] = useState(1);
  const [message, setMessage] = useState<string | null>(null);
  const [showAnimation, setShowAnimation] = useState(false);
  const [gachaResult, setGachaResult] = useState<CardSummary[] | null>(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [orbActivated, setOrbActivated] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const gachaMutation = useGachaDraw();
  const isDrawing = gachaMutation.isPending;

  const handleSummon = async () => {
    setMessage(null);
    setIsAnimating(true);
    
    // 카메라가 제단으로 줌인하는 애니메이션 시작
    setTimeout(() => {
      setOrbActivated(true);
      setIsSpinning(true);
    }, 800);

    // 오브 활성화 애니메이션 시간
    setTimeout(() => {
      setOrbActivated(false);
    }, 1400);

    try {
      const result = await gachaMutation.mutateAsync({ pulls });
      setGachaResult(result.cards);
      
      // 제단 작동 애니메이션 후 카드 공개
      setTimeout(() => {
        setIsSpinning(false);
        setIsAnimating(false);
        setShowAnimation(true);
      }, 2500);
    } catch (error) {
      setIsSpinning(false);
      setIsAnimating(false);
      setOrbActivated(false);
      setMessage(getErrorMessage(error));
    }
  };

  const handleAnimationComplete = () => {
    setShowAnimation(false);
    if (gachaResult) {
      const obtained = gachaResult
        .map((card) => `${card.grade} ${card.name}`)
        .join(', ');
      setMessage(`소환된 카드: ${obtained || '없음'}`);
      setGachaResult(null);
    }
  };

  const canAfford = (currency ?? 0) >= pulls * SUMMON_COST;
  const isSummonDisabled = !isAuthenticated || isDrawing || !canAfford;
  const disabledReason = !isAuthenticated
    ? 'GitHub로 로그인 후 이용해주세요.'
    : undefined;

  // WebGL 컨텍스트 최적화 설정
  const webglConfig = useMemo(() => {
    const mobile = isMobile();
    return {
      antialias: !mobile, // 모바일에서는 antialias 비활성화
      alpha: true,
      powerPreference: 'high-performance' as const,
      precision: mobile ? 'mediump' as const : 'highp' as const, // 모바일에서 정밀도 낮춤
      stencil: false, // 사용 안 하면 비활성화
      depth: true,
      logarithmicDepthBuffer: false, // 모바일에서 비활성화
      preserveDrawingBuffer: false, // 성능 향상
    };
  }, []);

  const optimalDPR = useMemo(() => getOptimalDPR(), []);

  return (
    <>
      {showAnimation && gachaResult && (
        <GachaAnimation
          cards={gachaResult}
          onComplete={handleAnimationComplete}
          onSkip={handleAnimationComplete}
        />
      )}

      <section className="gacha-machine-3d-section">
        <div className="gacha-machine-3d-container">
          <div className="gacha-machine-3d__header">
            <h2>카드 소환</h2>
            <p>제단에 기도하여 카드를 소환하세요!</p>
          </div>

          <div className="gacha-machine-3d__main">
            <div className="gacha-machine-3d__canvas-wrapper">
              <Canvas
                gl={webglConfig}
                dpr={optimalDPR}
                camera={{ position: [2.5, 1.5, 5], fov: 50 }}
                performance={{ min: 0.5 }} // 성능이 낮으면 자동으로 품질 낮춤
              >
                <SceneBackground />
                <Suspense fallback={null}>
                  <AnimatedCamera isAnimating={isAnimating} isSpinning={isSpinning} />
                  <SummonAltarScene
                    isActivated={orbActivated}
                    isSpinning={isSpinning}
                    isAnimating={isAnimating}
                  />
                </Suspense>
              </Canvas>
            </div>

            <div className="gacha-machine-3d__controls">
              <div className="gacha-machine-3d__pulls-control">
                <span className="gacha-machine-3d__pulls-label">소환하기</span>
                <div className="gacha-machine-3d__pulls-buttons">
                  <button
                    type="button"
                    className={`gacha-machine-3d__pulls-button ${pulls === 1 ? 'active' : ''}`}
                    onClick={() => setPulls(1)}
                    disabled={isDrawing}
                  >
                    1회 소환
                  </button>
                  <button
                    type="button"
                    className={`gacha-machine-3d__pulls-button ${pulls === 10 ? 'active' : ''}`}
                    onClick={() => setPulls(10)}
                    disabled={isDrawing}
                  >
                    10회 소환
                  </button>
                </div>
              </div>

              <div className="gacha-machine-3d__cost">
                <span className="gacha-machine-3d__cost-label">비용</span>
                <span className="gacha-machine-3d__cost-value">
                  {pulls * SUMMON_COST}
                </span>
                <span className="gacha-machine-3d__currency">
                  / 보유: {currency !== undefined ? currency.toLocaleString() : '—'}
                </span>
              </div>

              {/* 소환하기 버튼 */}
              <button
                type="button"
                className={`gacha-machine-3d__lever-button ${orbActivated ? 'pulled' : ''} ${isSpinning ? 'spinning' : ''} ${isSummonDisabled ? 'disabled' : ''}`}
                onClick={handleSummon}
                disabled={isSummonDisabled}
                title={disabledReason}
              >
                <span className="gacha-machine-3d__lever-button-text">
                  {isSpinning ? '소환 중...' : '소환하기'}
                </span>
              </button>

              {message && (
                <div className="gacha-machine-3d__message">{message}</div>
              )}

              {!isAuthenticated && (
                <div className="gacha-machine-3d__warning">
                  GitHub로 로그인하면 카드 소환을 이용할 수 있습니다.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <DonationSection />
    </>
  );
}

